<template>

    <cms-view>
        <cms-heading title="Overzicht loggers" icon="magicon-tiles">
            <cms-button color="positive" icon="magicon-plus" route="logger-add">Toevoegen</cms-button>
            <cms-upload-button v-if="userIsAdmin" icon="magicon-download" :start="true" allowed="csv" accept=".csv" :url="uploadCsvUrl" @complete="onUploadCsvComplete" @error="onUploadCsvError">Importeer .csv</cms-upload-button>
            <cms-dropdown v-if="userIsAdminOrAdvisor" class="button-dropdown" placeholder="Exporteren..." :options="exportOptions" @input="onExport"></cms-dropdown>
        </cms-heading>

        <cms-heading title="Filter resultaten" level="2">
            <cms-button :disabled="!hasFilter" @click="resetFilters">Reset filters</cms-button>
        </cms-heading>
        <complex-filter-panel :options="filterOptions" v-model="filter" @add="onFilterAdded" @remove="onFilterRemoved"></complex-filter-panel>

        <cms-heading :title="resultsTitle" level="2">
            <cms-button color="highlight-1" icon="magicon-ok-circle" :disabled="selectedLoggers.length === 0" @click="makeSelection">Selectie maken ({{ selectedLoggers.length }})</cms-button>
            <cms-button color="default-dark" icon="magicon-close" :disabled="selectedLoggers.length === 0" @click="resetSelection">Reset selectie</cms-button>
            <cms-searchbar v-model="searchQuery"></cms-searchbar>
        </cms-heading>

        <cms-table-panel v-spinner="loading" :item-count="loggers.totalCount || 0" :items-per-page="itemsPerPage" :offset="offset" @page="offset = arguments[0]" :placeholder="!loggers.items || !loggers.items.length" show-count="true" clickable="true">
            <cms-table-header>
                <cms-th field="serialNumber" v-model="sort">Serienummer</cms-th>
                <cms-th field="typeId" v-model="sort">Type</cms-th>
                <cms-th field="state" v-model="sort">Status</cms-th>
                <cms-th field="owner" v-model="sort">Eigendom</cms-th>
                <cms-th v-if="showAllocationData" field="allocationProjectCode" v-model="sort">Projectcode</cms-th>
                <cms-th field="mutationStartDate" v-model="sort">Startdatum</cms-th>
                <cms-th v-if="showAllocationData" field="allocationEndDate" v-model="sort">Verwachte einddatum</cms-th>
                <cms-th v-if="userIsAdminOrAdvisor" field="allocationActionDeadline" v-model="sort">Afh. termijn actie</cms-th>
                <cms-th v-if="showRepairData" field="repairReference" v-model="sort">Reparatie-batch</cms-th>
                <cms-th v-if="userIsAdminOrAdvisor" field="documentReference" v-model="sort">Document ref.</cms-th>
                <cms-th v-if="userIsAdminOrAdvisor" field="purchaseDate" v-model="sort">Datum aanschaf</cms-th>
                <cms-th v-if="userIsAdminOrAdvisor" field="purchaseValue" v-model="sort">Aanschafwaarde</cms-th>
                <cms-th field="comments" v-model="sort">Opmerkingen</cms-th>
                <cms-th style="width: 40px;"><cms-checkbox @input="toggleSelection" :checked="allLoggersChecked"></cms-checkbox></cms-th>
            </cms-table-header>
            <tr v-for="logger in loggers.items" :class="{ selected: isSelected(logger) }" @click="showLogger(logger)">
                <td>{{ logger.serialNumber }}</td>
                <td>{{ logger.type.title }}</td>
                <td><cms-text-label :title="logger.state | loggerState" :color="logger.state | loggerStateColor" /></td>
                <td>{{ logger.owner }}</td>
                <td v-if="showAllocationData">{{ logger.activeAllocation ? logger.activeAllocation.project.code : '' }}</td>
                <td>{{ (logger.latestMutation && logger.latestMutation.data.startDate ? logger.latestMutation.data.startDate : '') | date('LL') }}</td>
                <td v-if="showAllocationData">{{ (logger.activeAllocation ? logger.activeAllocation.endDate : '') | date('LL') }}</td>
                <td v-if="userIsAdminOrAdvisor">{{ logger.activeAllocation ? logger.activeAllocation.actionDeadline : null | actionDeadline(logger.activeAllocation ? logger.activeAllocation.project.actionDeadline : null) }}</td>
                <td v-if="showRepairData">{{ logger.activeRepair ? logger.activeRepair.reference : '' }}</td>
                <td v-if="userIsAdminOrAdvisor">{{ logger.documentReference }}</td>
                <td v-if="userIsAdminOrAdvisor">{{ logger.purchaseDate | date('LL') }}</td>
                <td v-if="userIsAdminOrAdvisor">{{ logger.purchaseValue | currency }}</td>
                <td>{{ logger.comments }}</td>
                <td @click.stop><cms-checkbox :checked="isSelected(logger)" @input="updateSelection(logger, arguments[0])"></cms-checkbox></td>
            </tr>
        </cms-table-panel>

    </cms-view>

</template>

<script>

    import Component from 'vue-class-component'
    import Vue from 'global-vue'
    import di from 'di'
    import gql from 'graphql-tag'

    import moment from 'moment'

    import without from 'lodash/without'
    import find from 'lodash/find'
    import assign from 'lodash/assign'
    import clone from 'lodash/clone'
    import omit from 'lodash/omit'
    import uniqBy from 'lodash/uniqBy'
    import differenceBy from 'lodash/differenceBy'
    import get from 'lodash/get'

    import numberFilter from '@wittignl/js-core/filters/number'

    import queryBase from 'app/queries/base'
    import LoggerState from 'app/enums/LoggerState'
    import RepairState from 'app/enums/RepairState'

    import LoggerQueries from 'app/queries/LoggerQueries'
    import LoggerTypeQueries from 'app/queries/LoggerTypeQueries'
    import ProjectQueries from 'app/queries/ProjectQueries'
    import RepairQueries from 'app/queries/RepairQueries'

    import UserRole from 'app/enums/UserRole'

    @Component({

        name: 'loggers',
        apollo: {

            loggers: LoggerQueries.all({

                fetchPolicy: 'cache-and-network',
                variables(){

                    return {
                        offset: this.offset,
                        limit: this.itemsPerPage,
                        filter: this.resolvedFilter,
                        repairIds: this.repairIds,
                        search: this.searchQuery,
                        sortField: this.sort.field,
                        sortDirection: this.sort.direction
                    }
                }
            }),

            repairs: RepairQueries.all({

                variables(){

                    return {
                        offset: null,
                        limit: null,
                        filter: {
                            state: RepairState.PENDING,
                        },
                        sortField: 'reference',
                        sortDirection: 'ASC'
                    }
                }
            }),
        }
    })
    export default class Loggers extends Vue
    {
        static FILTER_STORAGE_KEY = "LoggersFilter";
        static SORT_STORAGE_KEY = "LoggersSort";
        static SEARCH_STORAGE_KEY = "LoggersSearch";

        data(){

            return {

                loading: 0,

                selectedLoggers: [],

                loggers: {},
                repairs: {},

                offset: 0,
                filter: !this.filteredProjectCode ? this.$storage.getSession(Loggers.FILTER_STORAGE_KEY, {}) : {},
                searchQuery: this.$storage.getSession(Loggers.SEARCH_STORAGE_KEY, null),
                sort: this.$storage.getSession(Loggers.SORT_STORAGE_KEY, {
                    field: 'serialNumber',
                    direction: 'ASC'
                })
            }
        }

        created(){

            this.logger = this.$logger.child('Loggers');

            // Store filter in Session Storage
            this.$watch('filter', (filter) => {

                if(!this.filteredProjectCode) {
                    this.$storage.setSession(Loggers.FILTER_STORAGE_KEY, filter)
                }

                this.correctSort();
                this.offset = 0;

                this.resetSelection();
            });

            this.$watch('sort', (sort) => this.$storage.setSession(Loggers.SORT_STORAGE_KEY, sort));
            this.$watch('searchQuery', (searchQuery) => this.$storage.setSession(Loggers.SEARCH_STORAGE_KEY, searchQuery || ''));

            // Reset paging when searching
            this.$watch('searchQuery', () => {

                this.offset = 0;
                this.resetSelection();
            });

            this.$watch('repairIds', () => this.resetSelection());
        }

        breadcrumb(){

            return 'Loggers';
        }

        mounted(){

            if(this.filteredProjectCode){

                this.filter = {
                    state: [LoggerState.DEPLOYED],
                    allocationProjectCode: [{
                        operator: 'EQUALS',
                        value: this.filteredProjectCode
                    }]
                };
            }
        }

        resetFilters(){

            this.filter = {};
        }

        get currentYear(){

            return moment().format('Y');
        }

        get lastYear(){

            return moment().subtract(1, 'year').format('Y');
        }

        get resultsTitle(){

            return this.loading ? 'Resultaten' : `Resultaten (${numberFilter(this.loggers.totalCount || 0, 0)})`;
        }

        get userIsAdmin(){

            return this.$user.role === UserRole.ADMIN;
        }

        get userIsAdminOrAdvisor(){

            return [UserRole.ADVISOR, UserRole.ADMIN].indexOf(this.$user.role) >= 0;
        }

        get itemsPerPage(){

            return 20;
        }

        get hasFilter(){

            return Object.keys(this.filter).length > 0;
        }

        get filterOptions(){

            let filters = [
                {
                    title: 'Status',
                    key: 'state',
                    options: LoggerState.options()
                },
                {
                    title: 'Type logger',
                    key: 'typeTitle',
                    type: 'string'
                },
                {
                    title: 'Eigendom',
                    key: 'owner',
                    type: 'string'
                },
                {
                    title: 'Projectcode',
                    key: 'allocationProjectCode',
                    type: 'string'
                }
            ];

            const state = this.filter.state || [];

            if(state.indexOf(LoggerState.REPAIR_ACTIVE) >= 0){

                filters.push({
                    title: 'Reparatie batch',
                    key: 'repairId',
                    options: this.repairs.items ? this.repairs.items.map(type => ({ label: type.reference, value: type.id })) : []
                });
            }

            return filters;
        }

        get exportOptions(){

            return [
                { value: 'costs-current', label: `Exporteer kosten ${this.currentYear}` },
                { value: 'costs-last', label: `Exporteer kosten ${this.lastYear}` },
                { value: 'costs-distribution-current', label: `Exporteer kostenverdeling ${this.currentYear}` },
                { value: 'costs-distribution-last', label: `Exporteer kostenverdeling ${this.lastYear}` },
                { value: 'loggers', label: `Exporteer loggers` }
            ]
        }

        get resolvedFilter(){

            return omit(this.filter, ['repairId']);
        }

        get showAllocationData(){

            return (this.filter.state || []).indexOf(LoggerState.DEPLOYED) >= 0;
        }

        get showRepairData(){

            return (this.filter.state || []).indexOf(LoggerState.REPAIR_ACTIVE) >= 0;
        }

        get repairIds(){

            return this.filter.repairId || null;
        }

        get filteredProjectCode(){

            return this.$route.params.projectCode;
        }

        get uploadCsvUrl(){

            return this.$config.apiUrl + '/upload/csv/loggers'
        }

        get allLoggersChecked(){

            if(!this.loggers){
                return false;
            }

            return this.loggers.totalCount > 0 && this.loggers.totalCount ===  this.selectedLoggers.length;
        }

        updateSelection(logger, selected){

            if(selected) {
                this.selectedLoggers.push(logger);
            }
            else {

                const loggerItem = find(this.selectedLoggers, { id: logger.id });
                this.selectedLoggers = without(this.selectedLoggers, loggerItem);
            }
        }

        isSelected(logger){

            return !!find(this.selectedLoggers, { id: logger.id });
        }

        async toggleSelection(selected){

            if(selected){

                this.loading++;

                try {

                    const results = await this.$apollo.query(LoggerQueries.allSummary({

                        variables: {
                            filter: this.resolvedFilter,
                            repairIds: this.repairIds,
                            search: this.searchQuery
                        }
                    }));

                    const loggers = get(results, 'data.viewer.allLoggers.items');
                    this.selectedLoggers = uniqBy([...this.selectedLoggers, ...loggers], 'id');
                }
                catch(e){

                    this.logger.error(e);
                    this.$toast.error('De loggers konden niet geselecteerd worden');
                }

                this.loading--;
            }
            else {
                this.selectedLoggers = [];
            }
        }

        resetSelection(){

            this.selectedLoggers = [];
        }

        makeSelection(){

            if(this.selectedLoggers.length === 0){
                return;
            }

            di.set('selectedLoggers', this.selectedLoggers);
            this.$router.push({ name: 'select-loggers' });
        }

        showLogger(logger){

            this.$router.push({
                name: 'logger-view',
                params: { id: logger.id }
            });
        }

        correctSort(){

            const sort = this.sort.field;

            if((this.filter.state && sort === 'state') ||
                (!this.filter.state && sort === 'mutationStartDate') ||
                (!this.showAllocationData && ['allocationProjectCode', 'allocationEndDate'].indexOf(sort) >= 0) ||
                (!this.showRepairData && sort === 'repairReference')){

                this.sort.field = 'serialNumber';
            }
        }

        onQueryError(error){

            this.logger.error(error);
            this.$toast.error('De gegevens konden niet worden opgehaald');
        }

        onFilterAdded({ key }){

            if(key === 'allocationProjectCode' && (this.filter.state || []).indexOf(LoggerState.DEPLOYED) < 0){

                if(this.filter.state){
                    this.filter.state.push(LoggerState.DEPLOYED);
                }
                else {
                    this.filter.state = [LoggerState.DEPLOYED];
                }
            }
        }

        onFilterRemoved(filter){

            if(filter.key === 'state' && (this.filter.allocationProjectCode || []).length){

                var newFilter = clone(this.filter);
                delete newFilter['allocationProjectCode'];
                this.filter = newFilter;
            }
        }

        onUploadCsvComplete(){

            this.logger.info('Import CSV done');
            this.$toast.success('De loggers zijn geïmporteerd');

            this.refetchQuery('loggers');
        }

        onUploadCsvError(error){

            this.logger.error('Error importing CSV', error);
            this.$toast.error('De loggers konden niet worden geïmporteerd, wellicht is het formaat van de CSV file onjuist');
        }

        onExport(type){

            switch(type){

                case 'costs-current':
                    this.exportCosts(); break;

                case 'costs-last':
                    this.exportCosts(this.lastYear); break;

                case 'costs-distribution-current':
                    this.exportCostsDistribution(this.currentYear); break;

                case 'costs-distribution-last':
                    this.exportCostsDistribution(this.lastYear); break;

                case 'loggers':
                    this.exportLoggers(); break;
            }
        }

        exportCosts(year){

            window.open(this.$config.apiUrl + '/export/csv/costs' + (year ? '/'+year : ''));
        }

        exportCostsDistribution(year){

            window.open(this.$config.apiUrl + '/export/csv/costs-distribution' + (year ? '/'+year : ''));
        }

        exportLoggers(){

            let queryParams = [];

            queryParams.push('sortField=' + this.sort.field);
            queryParams.push('sortDirection=' + this.sort.direction);
            queryParams.push('filter=' + JSON.stringify(this.resolvedFilter));
            queryParams.push('search=' + (this.searchQuery || ''));

            window.open(this.$config.apiUrl + '/export/csv/loggers?' + encodeURI(queryParams.join('&')));
        }
    }

</script>
